import { useState } from 'react'
import { useBackgroundKeywordsQuery } from '@/api/hooks/use-background-keywords'
import type { BackgroundCmsKeyword } from '@/api/types/background'
import { MultiSelectChipCombobox } from '@/components/app/panels/right-panel/combo-box/multi-select-chip-combobox'

export type SearchBackgroundKeywordsComboboxProps = {
  /** Selected keyword ids (multi-select chips). */
  value: string[]
  onChange: (keywordIds: string[]) => void
  /** Reports open/close so a host panel can track "is one of my dropdowns open" (e.g. for an
   *  Escape-key priority chain) without owning this component's internal state. */
  onOpenChange?: (open: boolean) => void
}

function getKeywordId(keyword: BackgroundCmsKeyword): string {
  return String(keyword.cmsKeywordId)
}

function getKeywordLabel(keyword: BackgroundCmsKeyword): string {
  return keyword.cmsKeywordName
}

function keywordMatchesSearch(keyword: BackgroundCmsKeyword, query: string): boolean {
  return keyword.cmsKeywordName.toLowerCase().includes(query.toLowerCase())
}

export function SearchBackgroundKeywordsCombobox({
  value,
  onChange,
  onOpenChange,
}: SearchBackgroundKeywordsComboboxProps) {
  const [open, setOpen] = useState(false)

  function handleOpenChange(next: boolean) {
    setOpen(next)
    onOpenChange?.(next)
  }

  const {
    data: keywords = [],
    isLoading,
    isError,
    error,
  } = useBackgroundKeywordsQuery({
    enabled: open || value.length > 0,
  })

  return (
    <MultiSelectChipCombobox
      value={value}
      onChange={onChange}
      items={keywords}
      getId={getKeywordId}
      getLabel={getKeywordLabel}
      matchesSearch={keywordMatchesSearch}
      triggerLabel="Search keywords"
      searchPlaceholder="Search keywords"
      loadingMessage="Loading keywords…"
      errorMessage={error instanceof Error ? error.message : 'Failed to load keywords'}
      chipsTestId="background-keyword-filter-chips"
      isLoading={isLoading}
      isError={isError}
      open={open}
      onOpenChange={handleOpenChange}
      focusSearchOnOpen
    />
  )
}
